import { directSupabase } from '../lib/supabaseClient';
import {
  DEFAULT_UI_THEME,
  normalizeTokens,
  slugifyThemeKey,
  type JarvisUiThemeDefinition,
  type JarvisUiThemeTokens,
} from './tokens';

type ThemeRow = {
  id: string;
  theme_key: string;
  name: string;
  tokens: JarvisUiThemeTokens | Record<string, unknown>;
  updated_at?: string;
};

function fromRow(row: ThemeRow): JarvisUiThemeDefinition {
  return {
    id: String(row.id),
    key: String(row.theme_key),
    name: String(row.name || row.theme_key),
    builtin: false,
    tokens: normalizeTokens(row.tokens as Partial<JarvisUiThemeTokens>),
    updatedAt: row.updated_at ? String(row.updated_at) : undefined,
  };
}

export const uiThemeService = {
  async listThemes(): Promise<JarvisUiThemeDefinition[]> {
    const { data, error } = await directSupabase
      .from('jarvis_ui_themes')
      .select('id, theme_key, name, tokens, updated_at')
      .order('name', { ascending: true });
    if (error) {
      // Table may not exist until migration is applied — fall back to Default only.
      console.warn('jarvis_ui_themes list failed:', error.message);
      return [DEFAULT_UI_THEME];
    }
    const customs = (data || []).map((row) => fromRow(row as ThemeRow));
    return [DEFAULT_UI_THEME, ...customs];
  },

  async getActiveThemeKey(): Promise<string> {
    const { data, error } = await directSupabase
      .from('jarvis_ui_settings')
      .select('active_theme_key')
      .eq('id', 1)
      .maybeSingle();
    if (error) {
      console.warn('jarvis_ui_settings read failed:', error.message);
      return 'default';
    }
    return String(data?.active_theme_key || 'default');
  },

  async resolveActiveTheme(): Promise<JarvisUiThemeDefinition> {
    const activeKey = await this.getActiveThemeKey();
    if (!activeKey || activeKey === 'default') return DEFAULT_UI_THEME;
    const { data, error } = await directSupabase
      .from('jarvis_ui_themes')
      .select('id, theme_key, name, tokens, updated_at')
      .eq('theme_key', activeKey)
      .maybeSingle();
    if (error) {
      console.warn('jarvis_ui_themes resolve failed:', error.message);
      return DEFAULT_UI_THEME;
    }
    if (!data) return DEFAULT_UI_THEME;
    return fromRow(data as ThemeRow);
  },

  async setActiveThemeKey(themeKey: string): Promise<void> {
    const key = String(themeKey || 'default').trim() || 'default';
    const { data: userRes } = await directSupabase.auth.getUser();
    const { error } = await directSupabase
      .from('jarvis_ui_settings')
      .upsert({
        id: 1,
        active_theme_key: key,
        updated_at: new Date().toISOString(),
        updated_by: userRes.user?.id ?? null,
      });
    if (error) throw new Error(error.message);
  },

  async createTheme(name: string, tokens: Partial<JarvisUiThemeTokens>): Promise<JarvisUiThemeDefinition> {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Theme name is required');
    let themeKey = slugifyThemeKey(trimmed);
    if (themeKey === 'default') themeKey = `custom-${Date.now().toString(36)}`;

    const { data: userRes } = await directSupabase.auth.getUser();
    const payload = {
      theme_key: themeKey,
      name: trimmed,
      tokens: normalizeTokens(tokens),
      created_by: userRes.user?.id ?? null,
    };
    const { data, error } = await directSupabase
      .from('jarvis_ui_themes')
      .insert(payload)
      .select('id, theme_key, name, tokens, updated_at')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to create theme');
    return fromRow(data as ThemeRow);
  },

  async updateTheme(
    themeKey: string,
    patch: { name?: string; tokens?: Partial<JarvisUiThemeTokens> },
  ): Promise<JarvisUiThemeDefinition> {
    if (themeKey === 'default') throw new Error('The Default theme cannot be edited');
    const updates: Record<string, unknown> = {};
    if (patch.name != null) updates.name = String(patch.name).trim();
    if (patch.tokens) updates.tokens = normalizeTokens(patch.tokens);
    const { data, error } = await directSupabase
      .from('jarvis_ui_themes')
      .update(updates)
      .eq('theme_key', themeKey)
      .select('id, theme_key, name, tokens, updated_at')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Theme not found');
    return fromRow(data as ThemeRow);
  },

  async deleteTheme(themeKey: string): Promise<void> {
    if (themeKey === 'default') throw new Error('The Default theme cannot be deleted');
    const active = await this.getActiveThemeKey();
    if (active === themeKey) {
      await this.setActiveThemeKey('default');
    }
    const { error } = await directSupabase
      .from('jarvis_ui_themes')
      .delete()
      .eq('theme_key', themeKey);
    if (error) throw new Error(error.message);
  },
};
